/* eslint-disable no-console */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomModal from "../../Core/Components/Modal/CustomModal";
import ChargerIcon from "../../Common/Svgs/ChargerIcon";
import CloseIcon from "../../Common/Svgs/CloseIcon";
import CustomInput from "../../Core/Components/CustomInput";
import Button from "../../Core/Components/CustomButton";
import { client } from "../../Utils/axiosClient";
import { updateRental } from "../../Redux/Slices/rentals.slice";
import { getRentalList } from "../../Redux/APIs/rentalsAPI";

const ColumnHeader = () => {
  return (
    <div className="flex items-center border-b-[0.5px] border-light-grey">
      {["Registration no.", "Hub Station", "Start Energy", "End Energy"].map(
        (item) => (
          <div key={item} className="flex-1 px-6 py-3 bg-background">
            <p className="font-aileron text-10 font-normal text-dark-gray">
              {item}
            </p>
          </div>
        ),
      )}
    </div>
  );
};

const MoveToHubStationModal = ({ visible, onCancel }) => {
  const dispatch = useDispatch();

  const { currentRental } = useSelector((state) => state.rentals);

  const [batteries, setBatteries] = useState([]);
  const [errors, setErrors] = useState([]);

  // handing the end energy percent
  const handleEndEnergy = (e, id) => {
    setBatteries((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, endEnergy: e.target.value } : item,
      ),
    );
  };

  // focus when entering end energy
  const handleFocus = (id, focus) => {
    setBatteries((prev) =>
      prev.map((item) => (item.id === id ? { ...item, focus } : item)),
    );
  };

  // clear states and close the modal
  const handleCancel = () => {
    setBatteries([]);
    setErrors([]);
    onCancel();
  };

  // check if the end energy is not added
  const checkErrors = () => {
    const errorIds = batteries
      .filter((item) => !item.endEnergy)
      .map((item) => item.id);

    if (errorIds.length > 0) {
      setErrors(errorIds);
      return true;
    }

    return false;
  };

  // API call to dispatch
  const handleConfirm = async () => {
    const hasError = checkErrors();

    if (hasError) return;

    const payload = {
      rentalId: currentRental.id,
      batteries,
    };

    try {
      dispatch(updateRental({ key: "loading", value: true }));

      await client.post(
        `${process.env.REACT_APP_API_URL}/rentals/move-to-hub-station`,
        payload,
      );

      await dispatch(getRentalList());

      dispatch(
        updateRental({
          key: "showToastMessage",
          value: {
            visible: true,
            data: {
              title: "1 Trike Moved to Hub Station.",
              description: `Trike ${currentRental?.trikeRegistrationNo} was move to hub station successfully.`,
            },
          },
        }),
      );
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(updateRental({ key: "loading", value: false }));
      handleCancel();
    }
  };

  useEffect(() => {
    const local = currentRental?.batteries
      ?.filter((b) => b.isActive)
      ?.map((item) => ({
        id: item?.id,
        registrationNo: item?.registrationNo,
        startEnergy: item?.startEnergy,
        endEnergy: item?.startEnergy ? item?.startEnergy : "",
        focus: false,
      }));

    setBatteries(local);
  }, [currentRental]);

  return (
    <CustomModal visible={visible} onCancel={handleCancel} width="1121px">
      <div
        className="py-4"
        style={{
          boxShadow:
            "12px 12px 16px -4px rgba(16, 24, 40, 0.08), -4px -4px 6px -2px rgba(16, 24, 40, 0.03)",
        }}
      >
        <div className="px-4 flex items-center justify-between">
          <div className="flex items-center gap-x-3">
            <div className="flex items-center justify-center w-12 h-12 bg-green rounded-full">
              <ChargerIcon size={24} className="text-yellow" />
            </div>

            <p className="font-poppins text-base font-medium">
              Move To Hub Station
            </p>
          </div>

          <button type="button" onClick={handleCancel}>
            <CloseIcon className="w-6 h-6 stroke-dark-gray" />
          </button>
        </div>

        <div className="max-h-[450px] overflow-y-scroll">
          <div className="mt-6">
            <p className="px-4 font-poppins font-medium text-xs">
              Enter energy levels of the batteries
            </p>

            <p className="mt-1 mb-6 px-4 font-aileron font-normal text-xs text-dark-gray">
              Record the energy consumed by the batteries for calculating energy
              total revenue generation.
            </p>

            <ColumnHeader type="default" />

            {batteries?.map((item) => {
              const hasError = errors.includes(item.id);

              let border;

              if (hasError) {
                border = "error";
              } else if (item.focus) {
                border = "success";
              } else {
                border = "primary";
              }

              return (
                <div
                  key={item.id}
                  className="flex items-center py-2 h-12 border-b-[0.5px] border-light-grey"
                >
                  <p className="flex-1 px-6 font-aileron text-xs font-semibold text-black">
                    {item?.registrationNo}
                  </p>

                  <p className="flex-1 px-6 font-aileron text-xs font-semibold text-black capitalize">
                    {currentRental?.hubName}
                  </p>

                  <div className="flex-1 px-6 flex items-center">
                    <ChargerIcon size={16} className="text-green" />
                    <p className="ml-2 font-aileron text-10 font-semibold text-green">
                      {item?.startEnergy || "--"} kWh
                    </p>
                  </div>

                  <div className="flex-1 px-6 flex items-center">
                    <CustomInput
                      intent="editCommodity"
                      text="generalSettings"
                      value={item.endEnergy}
                      focus={item.focus}
                      border={border}
                      placeholder={item.focus ? null : "Energy"}
                      onChange={(e) => handleEndEnergy(e, item.id)}
                      onFocus={() => handleFocus(item.id, true)}
                      onBlur={() => handleFocus(item.id, false)}
                    />

                    <p className="ml-2 flex-1 font-aileron font-semibold text-xs text-dark-gray capitalize">
                      kWh
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-4 mt-6 flex justify-end">
          <div className="flex items-center gap-x-2">
            <Button
              size="filter"
              width="dynamic"
              colors="grey"
              onClick={handleCancel}
            >
              Cancel
            </Button>

            <Button
              size="filter"
              width="dynamic"
              type="submit"
              onClick={handleConfirm}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default MoveToHubStationModal;
